/**
 * AdminWorkOrdersBoard
 * --------------------
 * Unified command-center view for ALL maintenance/work-order requests
 * coming from the hospital portal, admin manual entry, or the public
 * homepage form. Direct mirror of AdminBackordersBoard:
 *
 *   ┌──────────────────────────────────────────────────────────────────┐
 *   │ KPI strip (clickable filters)                       [+ New WO]   │
 *   ├──────────────────────────────────────────────────────────────────┤
 *   │ Status tabs · Search · Refresh                                   │
 *   ├──────────────────────────────────────────────────────────────────┤
 *   │ Request list (table)                                             │
 *   │   Row click → slide-out detail drawer with action toolbar        │
 *   │     - Send to Contractors (RFQ broadcast — same flow as backords)│
 *   │     - View contractor quotes                                     │
 *   │     - Award                                                      │
 *   └──────────────────────────────────────────────────────────────────┘
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/App";
import {
  Search,
  Plus,
  RefreshCw,
  Mail,
  Send,
  X,
  Eye,
  AlertCircle,
  CheckCircle2,
  Clock,
  Wrench,
  Loader2,
  ListChecks,
  Pause,
  Trash2,
  Users,
  Award,
  Building2,
  MapPin,
  ExternalLink,
  ChevronRight,
  Pencil,
  Save,
  UserPlus,
  Calculator,
  FileDown,
  Paperclip,
  Building2 as Hospital,
  Maximize2,
  Minimize2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import CrmContactPicker from "@/components/admin/CrmContactPicker";
import WorkOrderBuildOrderTab from "@/components/admin/WorkOrderBuildOrderTab";
import WorkOrderControlTab from "@/components/admin/WorkOrderControlTab";
import SupplierPayablesPanel from "@/components/admin/SupplierPayablesPanel";

const API = process.env.REACT_APP_BACKEND_URL;

const ISSUE_TYPES = [
  { id: "water_leak", label: "Water Leak" },
  { id: "equipment_malfunction", label: "Equipment Malfunction" },
  { id: "hvac_issue", label: "HVAC Issue" },
  { id: "plumbing", label: "Plumbing" },
  { id: "electrical", label: "Electrical" },
  { id: "filter_replacement", label: "Filter Replacement" },
  { id: "preventive_maintenance", label: "Preventive Maintenance" },
  { id: "other", label: "Other" },
];

const STAGE_OPTIONS = [
  "new",
  "dispatching",
  "sent_to_contractors",
  "quotes_received",
  "awarded",
  "in_progress",
  "completed",
  "signed_off",
  "on_hold",
];

const PRIORITY_OPTIONS = ["low", "normal", "high", "emergency"];

const STATUS_META = {
  new:                 { label: "New",         cls: "bg-blue-50 text-blue-700 border-blue-200" },
  intake:              { label: "Intake",      cls: "bg-blue-50 text-blue-700 border-blue-200" },
  pending:             { label: "Pending",     cls: "bg-blue-50 text-blue-700 border-blue-200" },
  dispatching:         { label: "Dispatching", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  sent_to_contractors: { label: "RFQ Sent",    cls: "bg-amber-50 text-amber-700 border-amber-200" },
  quotes_received:     { label: "Quotes In",   cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  awarded:             { label: "Awarded",     cls: "bg-violet-50 text-violet-700 border-violet-200" },
  scheduled:           { label: "Scheduled",   cls: "bg-sky-50 text-sky-700 border-sky-200" },
  dispatched:          { label: "Dispatched",  cls: "bg-violet-50 text-violet-700 border-violet-200" },
  delayed:             { label: "Delayed",     cls: "bg-amber-50 text-amber-700 border-amber-200" },
  in_progress:         { label: "In Progress", cls: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  completed:           { label: "Completed",   cls: "bg-slate-100 text-slate-700 border-slate-300" },
  signed_off:          { label: "Signed Off",  cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  on_hold:             { label: "On Hold",     cls: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  cancelled:           { label: "Cancelled",   cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

const SOURCE_META = {
  hospital_portal: { label: "Hospital", cls: "bg-violet-50 text-violet-700" },
  manual:          { label: "Manual",   cls: "bg-amber-50 text-amber-700" },
  public:          { label: "Web Form", cls: "bg-blue-50 text-blue-700" },
};

const PRIORITY_META = {
  emergency: "bg-red-100 text-red-700",
  urgent:    "bg-red-100 text-red-700",
  high:      "bg-orange-100 text-orange-700",
  normal:    "bg-blue-100 text-blue-700",
  low:       "bg-slate-100 text-slate-600",
};

function fmtAge(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function StatusPill({ status }) {
  const meta = STATUS_META[status] || { label: status || "—", cls: "bg-slate-100 text-slate-600 border-slate-200" };
  return (
    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded border ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

function SourceBadge({ source }) {
  const meta = SOURCE_META[source] || { label: source || "—", cls: "bg-slate-100 text-slate-500" };
  return (
    <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

// ============================================================================
// Detail Drawer
// ============================================================================
function WorkOrderDrawer({ id, token, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("summary"); // summary | quotes | order | control | payables
  const [drawerExpanded, setDrawerExpanded] = useState(false);
  const [sourcing, setSourcing] = useState(false);
  const [contractors, setContractors] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [draftBody, setDraftBody] = useState("");
  const [draftSubject, setDraftSubject] = useState("");
  const [showRfq, setShowRfq] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showManualQuote, setShowManualQuote] = useState(false);
  const [showHospitalQuote, setShowHospitalQuote] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [newContractors, setNewContractors] = useState([]); // {company_name, contact_name, email, phone}

  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/work-orders/${id}`, { headers });
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load work order");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const openRfq = async () => {
    try {
      const r = await axios.get(`${API}/api/maintenance-sourcing/${id}/preview`, { headers });
      setContractors(r.data.contractors || []);
      setDraftBody(r.data.draft_body || "");
      setDraftSubject(r.data.draft_subject || "");
      setPicked(new Set());
      setShowRfq(true);
    } catch (e) {
      toast.error("Failed to load contractors");
    }
  };

  const broadcast = async () => {
    // Validate new contractor entries
    const cleanNew = newContractors
      .filter((c) => (c.company_name || "").trim() && (c.email || "").trim())
      .map((c) => ({
        company_name: c.company_name.trim(),
        contact_name: (c.contact_name || "").trim() || undefined,
        email: c.email.trim(),
        phone: (c.phone || "").trim() || undefined,
      }));
    if (picked.size === 0 && cleanNew.length === 0) {
      toast.error("Pick a contractor or add a new one");
      return;
    }
    setSourcing(true);
    try {
      const r = await axios.post(
        `${API}/api/maintenance-sourcing/${id}/broadcast`,
        {
          contractor_ids: [...picked],
          new_contractors: cleanNew,
          subject: draftSubject,
          body: draftBody,
        },
        { headers },
      );
      if (r.data.mailto) window.open(r.data.mailto, "_blank");
      const addedNote = r.data.new_contractors_created?.length
        ? ` (added ${r.data.new_contractors_created.length} new contractor${r.data.new_contractors_created.length === 1 ? "" : "s"} to directory)`
        : "";
      toast.success(`RFQ queued for ${r.data.recipient_count} recipient${r.data.recipient_count === 1 ? "" : "s"} in Outlook${addedNote}`);
      setShowRfq(false);
      setNewContractors([]);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Broadcast failed");
    } finally {
      setSourcing(false);
    }
  };

  const award = async (quote) => {
    const defaultAmount = quote.awarded_amount ?? quote.total_estimate ?? "";
    const input = window.prompt(
      `Award amount $ for ${quote.contractor_name}? (Leave as-is to use $${defaultAmount}. You can award multiple contractors with different splits.)`,
      String(defaultAmount || ""),
    );
    if (input === null) return;
    const amount = input.trim() ? Number(input.trim()) : null;
    if (input.trim() && Number.isNaN(amount)) {
      toast.error("Invalid amount");
      return;
    }
    try {
      const r = await axios.post(
        `${API}/api/admin/maintenance-requests/${id}/award/${quote.id}`,
        { award_amount: amount },
        { headers },
      );
      toast.success(`Awarded · rolling total $${(r.data.rolling_total || 0).toLocaleString()}`);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Award failed");
    }
  };

  const needInfo = async (quote) => {
    const question = window.prompt(`Question for ${quote.contractor_name}:`);
    if (!question || !question.trim()) return;
    try {
      await axios.post(
        `${API}/api/admin/maintenance-requests/${id}/contractor-quotes/${quote.id}/need-info`,
        { question: question.trim() },
        { headers },
      );
      toast.success("Question sent to contractor");
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    }
  };

  const reject = async (quote) => {
    const reason = window.prompt(
      `Reject ${quote.contractor_name}'s bid? (Optional reason — they'll see this in their email.)`,
    );
    if (reason === null) return;
    try {
      await axios.post(
        `${API}/api/admin/maintenance-requests/${id}/contractor-quotes/${quote.id}/reject`,
        { reason: reason || null },
        { headers },
      );
      toast.success("Bid rejected — contractor notified");
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reject failed");
    }
  };

  const cancelWorkOrder = async () => {
    const reason = window.prompt(
      "Cancel this work order? (Soft-delete — hidden from default views but kept for audit.)\n\nOptional: reason",
    );
    if (reason === null) return;
    setCancelling(true);
    try {
      await axios.post(
        `${API}/api/admin/work-orders/${id}/cancel`,
        { reason: reason || null },
        { headers },
      );
      toast.success("Work order cancelled");
      onClose?.();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancel failed");
    } finally {
      setCancelling(false);
    }
  };

  const req = data?.request;
  const quotes = data?.contractor_quotes || [];

  return (
    <div className="fixed inset-0 z-40 flex" data-testid="wo-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className={`bg-white shadow-2xl flex flex-col border-l border-slate-200 transition-all duration-200 ${
        drawerExpanded ? "w-full max-w-[78vw]" : "w-full max-w-2xl"}`}>
        <div className="flex items-center justify-between px-5 h-14 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2">
            <Wrench className="w-5 h-5 text-blue-600" />
            <span className="font-semibold text-slate-900">{id}</span>
            {req?.status && <StatusPill status={req.status || req.stage} />}
            {req?.parts_on_backorder && (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-rose-100 text-rose-800 border border-rose-300"
                title="Waiting on backordered parts"
                data-testid="wo-drawer-parts-backorder-badge"
              >
                <AlertCircle className="w-3 h-3" /> Parts on backorder
              </span>
            )}
            {req?.priority && (
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${PRIORITY_META[req.priority] || "bg-slate-100 text-slate-600"}`}>
                {req.priority}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={() => setDrawerExpanded((v) => !v)}
              className="p-1.5 hover:bg-slate-200 rounded text-slate-500"
              title={drawerExpanded ? "Collapse to half-page" : "Expand to full page"}
              data-testid="wo-drawer-expand-toggle"
            >
              {drawerExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
            <button onClick={onClose} className="p-1.5 hover:bg-slate-200 rounded" data-testid="wo-drawer-close">
              <X className="w-4 h-4 text-slate-600" />
            </button>
          </div>
        </div>

        {/* Action toolbar */}
        <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-200 bg-white flex-wrap">
          <Button size="sm" onClick={openRfq} className="bg-amber-600 hover:bg-amber-700 gap-1.5" data-testid="wo-send-contractors-btn">
            <Send className="w-3.5 h-3.5" /> Send to Contractors
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowManualQuote(true)} className="gap-1.5" data-testid="wo-add-manual-quote-btn">
            <UserPlus className="w-3.5 h-3.5" /> Add Quote
          </Button>
          <Button size="sm" variant="outline" onClick={() => setTab("quotes")} className="gap-1.5" data-testid="wo-view-quotes-btn">
            <ListChecks className="w-3.5 h-3.5" /> Quotes ({quotes.length})
          </Button>
          {quotes.length > 0 && (
            <Button
              size="sm"
              onClick={() => setShowHospitalQuote(true)}
              className="bg-emerald-600 hover:bg-emerald-700 gap-1.5"
              data-testid="wo-quote-hospital-btn"
            >
              <Calculator className="w-3.5 h-3.5" /> Quote Hospital
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => setShowEdit(true)} className="gap-1.5" data-testid="wo-edit-btn">
            <Pencil className="w-3.5 h-3.5" /> Edit
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              window.open(`/admin/work-orders/${id}`, "_blank");
            }}
            className="gap-1.5 border-violet-300 text-violet-700 hover:bg-violet-50"
            data-testid="wo-open-full-view-btn"
            title="Open the full WO Detail page — Hospital Contacts, Incidents, Updates, Quotes, Sign-off"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Full view
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={cancelWorkOrder}
            disabled={cancelling || req?.status === "cancelled"}
            className="gap-1.5 text-rose-600 hover:text-rose-700 hover:bg-rose-50 border-rose-200 ml-auto"
            data-testid="wo-cancel-btn"
          >
            {cancelling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            Cancel WO
          </Button>
        </div>

        <div className="flex border-b border-slate-200 bg-white px-5">
          {[
            { id: "summary", label: "Summary" },
            { id: "quotes", label: `Contractor Quotes (${quotes.length})` },
            { id: "order", label: "Build Order" },
            { id: "control", label: "Work Control Center" },
            { id: "payables", label: "Payables" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => {
                setTab(t.id);
                if (["order", "control", "payables"].includes(t.id)) setDrawerExpanded(true);
              }}
              className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id
                  ? "border-blue-600 text-blue-700"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
              data-testid={`wo-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading && (
            <div className="text-center py-10 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
            </div>
          )}

          {!loading && tab === "summary" && req && (
            <>
              <Section title="Site">
                <KV label="Hospital" value={req.hospital_name} icon={Building2} />
                <KV label="Facility" value={req.facility_name} />
                <KV label="Location" value={req.location_details || req.location} icon={MapPin} />
              </Section>

              <Section title="Requested by">
                <KV label="Contact" value={req.contact_name} />
                <KV label="Email" value={req.contact_email || req.submitted_by_email} />
                <KV label="Phone" value={req.contact_phone} />
              </Section>

              <Section title="Issues">
                {(req.items && req.items.length > 0) ? (
                  <div className="space-y-2">
                    {req.items.map((it, idx) => (
                      <div key={idx} className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                            {(it.request_type || "issue").replace(/_/g, " ")}
                          </span>
                          {it.location_details && (
                            <span className="text-xs text-slate-500 flex items-center gap-1">
                              <MapPin className="w-3 h-3" /> {it.location_details}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-800">{it.description}</p>
                        {it.notes && <p className="text-xs text-slate-500 mt-1 italic">Notes: {it.notes}</p>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                    <p className="text-sm text-slate-800">{req.description || "(no description)"}</p>
                  </div>
                )}
              </Section>

              {req.photos?.length > 0 && (
                <Section title={`Photos (${req.photos.length})`}>
                  <div className="grid grid-cols-3 gap-2">
                    {req.photos.map((url, i) => (
                      <a key={i} href={url} target="_blank" rel="noreferrer" className="block">
                        <img src={url} alt="" className="w-full h-20 object-cover rounded border border-slate-200" />
                      </a>
                    ))}
                  </div>
                </Section>
              )}

              {req.sourcing_broadcasts?.length > 0 && (
                <Section title="RFQ History">
                  <div className="space-y-1.5">
                    {req.sourcing_broadcasts.map((b, i) => (
                      <div key={i} className="text-xs text-slate-600 bg-slate-50 rounded p-2">
                        <strong>{fmtAge(b.broadcast_at)}</strong> — sent to {b.recipient_count} contractor{b.recipient_count === 1 ? "" : "s"} by {b.broadcast_by}
                      </div>
                    ))}
                  </div>
                </Section>
              )}
            </>
          )}

          {!loading && tab === "quotes" && (
            <>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  Contractor Quotes
                </h3>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowManualQuote(true)}
                  className="gap-1.5"
                  data-testid="wo-quotes-add-manual-btn"
                >
                  <Plus className="w-3.5 h-3.5" /> Add manual quote
                </Button>
              </div>
              {quotes.length === 0 ? (
                <div className="text-center py-10 text-slate-500">
                  <ListChecks className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                  <p className="text-sm">No contractor quotes yet.</p>
                  <p className="text-xs text-slate-400 mt-1">Send the RFQ to start collecting bids.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {quotes.map((q) => (
                    <div
                      key={q.id}
                      className={`border rounded-xl p-4 ${
                        q.status === "awarded"
                          ? "border-emerald-300 bg-emerald-50"
                          : q.status === "not_selected"
                          ? "border-slate-200 bg-slate-50 opacity-70"
                          : q.status === "awaiting_info"
                          ? "border-amber-300 bg-amber-50"
                          : "border-slate-200 bg-white"
                      }`}
                      data-testid={`wo-quote-${q.id}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-semibold text-slate-900">{q.contractor_name}</p>
                          <p className="text-xs text-slate-500">{q.contractor_email}</p>
                          {q.contractor_phone && (
                            <p className="text-xs text-slate-500">{q.contractor_phone}</p>
                          )}
                        </div>
                        <div className="text-right">
                          {q.total_estimate != null && (
                            <p className="text-lg font-bold text-slate-900">${Number(q.total_estimate).toLocaleString()}</p>
                          )}
                          {q.lead_time && <p className="text-xs text-slate-500">{q.lead_time}</p>}
                          {q.status === "awarded" && q.awarded_amount != null && Number(q.awarded_amount) !== Number(q.total_estimate) && (
                            <p className="text-xs text-emerald-700 font-semibold mt-0.5">
                              Awarded ${Number(q.awarded_amount).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                      {q.notes && <p className="text-xs text-slate-600 italic mb-2">{q.notes}</p>}
                      {q.thread?.length > 0 && (
                        <div className="text-xs space-y-1 mb-2 border-l-2 border-slate-200 pl-2">
                          {q.thread.slice(-3).map((t) => (
                            <div key={t.id} className="text-slate-600">
                              <span className="font-semibold">{t.kind === "need_info" ? "Q: " : ""}</span>
                              {t.message}
                              <span className="text-slate-400"> — {fmtAge(t.at)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                          {q.status === "awarded" ? "✓ Awarded" :
                           q.status === "not_selected" ? "✗ Not selected" :
                           q.status === "awaiting_info" ? "⏳ Awaiting info" :
                           fmtAge(q.created_at)}
                        </span>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {q.status !== "not_selected" && (
                            <Button
                              size="sm"
                              onClick={() => award(q)}
                              className="bg-violet-600 hover:bg-violet-700 gap-1 h-7 px-2.5 text-xs"
                              data-testid={`wo-award-${q.id}`}
                            >
                              <Award className="w-3 h-3" /> {q.status === "awarded" ? "Re-award" : "Award"}
                            </Button>
                          )}
                          {q.status !== "not_selected" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => needInfo(q)}
                              className="gap-1 h-7 px-2.5 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                              data-testid={`wo-needinfo-${q.id}`}
                            >
                              <Mail className="w-3 h-3" /> Need info
                            </Button>
                          )}
                          {q.status !== "not_selected" && q.status !== "awarded" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => reject(q)}
                              className="gap-1 h-7 px-2.5 text-xs border-rose-300 text-rose-700 hover:bg-rose-50"
                              data-testid={`wo-reject-${q.id}`}
                            >
                              <X className="w-3 h-3" /> Reject
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {!loading && tab === "order" && req && (
            <WorkOrderBuildOrderTab workOrder={req} token={token} onRefresh={load} />
          )}

          {!loading && tab === "control" && req && (
            <WorkOrderControlTab workOrder={req} token={token} onRefresh={load} />
          )}

          {!loading && tab === "payables" && req && (
            <SupplierPayablesPanel
              linkType="work_order"
              linkId={req.id}
              token={token}
              defaultSupplier={{
                name: req.awarded_contractor_name,
                email: req.awarded_contractor_email,
              }}
            />
          )}
        </div>
      </div>

      {/* RFQ Broadcast Modal */}
      {showRfq && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="wo-rfq-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-5 h-14 border-b border-slate-200">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <Send className="w-4 h-4 text-amber-600" /> Send RFQ to Contractors
              </h3>
              <button onClick={() => setShowRfq(false)} className="p-1.5 hover:bg-slate-100 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-4 overflow-y-auto">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Contractors</label>
                <div className="mt-2 max-h-48 overflow-y-auto border border-slate-200 rounded-lg divide-y">
                  {contractors.length === 0 ? (
                    <p className="text-sm text-slate-500 p-3">No contractors on file with email.</p>
                  ) : (
                    contractors.map((c) => {
                      const isPicked = picked.has(c.id);
                      return (
                        <label
                          key={c.id}
                          className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer"
                          data-testid={`wo-rfq-contractor-${c.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={isPicked}
                            onChange={() => {
                              setPicked((prev) => {
                                const next = new Set(prev);
                                if (next.has(c.id)) next.delete(c.id); else next.add(c.id);
                                return next;
                              });
                            }}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">{c.name}</p>
                            <p className="text-xs text-slate-500 truncate">{c.email}</p>
                          </div>
                        </label>
                      );
                    })
                  )}
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  {picked.size} selected
                  {newContractors.filter((c) => c.company_name && c.email).length > 0 &&
                    ` · ${newContractors.filter((c) => c.company_name && c.email).length} new`}
                </p>
              </div>

              {/* + Add new contractor (ad-hoc) */}
              <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/50">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    New contractor (not in directory)
                  </label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setNewContractors((p) => [...p, { company_name: "", contact_name: "", email: "", phone: "" }])
                    }
                    data-testid="wo-rfq-add-new-contractor-btn"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add row
                  </Button>
                </div>
                {newContractors.length === 0 ? (
                  <p className="text-xs text-slate-500">Click "Add row" to invite a contractor who isn't on file yet. They'll be auto-added to the directory.</p>
                ) : (
                  <div className="space-y-2">
                    {newContractors.map((c, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-1.5 items-start" data-testid={`wo-rfq-new-row-${idx}`}>
                        <Input
                          placeholder="Company name *"
                          value={c.company_name}
                          onChange={(e) =>
                            setNewContractors((p) => {
                              const n = [...p];
                              n[idx] = { ...n[idx], company_name: e.target.value };
                              return n;
                            })
                          }
                          className="h-8 text-xs col-span-3"
                          data-testid={`wo-rfq-new-company-${idx}`}
                        />
                        <Input
                          placeholder="Contact name"
                          value={c.contact_name}
                          onChange={(e) =>
                            setNewContractors((p) => {
                              const n = [...p];
                              n[idx] = { ...n[idx], contact_name: e.target.value };
                              return n;
                            })
                          }
                          className="h-8 text-xs col-span-3"
                        />
                        <Input
                          type="email"
                          placeholder="email * (will receive RFQ)"
                          value={c.email}
                          onChange={(e) =>
                            setNewContractors((p) => {
                              const n = [...p];
                              n[idx] = { ...n[idx], email: e.target.value };
                              return n;
                            })
                          }
                          className="h-8 text-xs col-span-3"
                          data-testid={`wo-rfq-new-email-${idx}`}
                        />
                        <Input
                          placeholder="Phone"
                          value={c.phone}
                          onChange={(e) =>
                            setNewContractors((p) => {
                              const n = [...p];
                              n[idx] = { ...n[idx], phone: e.target.value };
                              return n;
                            })
                          }
                          className="h-8 text-xs col-span-2"
                        />
                        <button
                          type="button"
                          onClick={() => setNewContractors((p) => p.filter((_, i) => i !== idx))}
                          className="col-span-1 text-rose-500 hover:bg-rose-50 rounded p-1 flex items-center justify-center"
                          aria-label="Remove row"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Subject</label>
                <Input value={draftSubject} onChange={(e) => setDraftSubject(e.target.value)} className="mt-1" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Body</label>
                <Textarea value={draftBody} onChange={(e) => setDraftBody(e.target.value)} rows={10} className="mt-1 font-mono text-xs" />
              </div>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setShowRfq(false)}>Cancel</Button>
              <Button
                onClick={broadcast}
                disabled={sourcing || (picked.size === 0 && newContractors.filter((c) => c.company_name && c.email).length === 0)}
                className="bg-amber-600 hover:bg-amber-700 gap-1.5"
                data-testid="wo-rfq-send-btn"
              >
                {sourcing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                Open in Outlook ({picked.size + newContractors.filter((c) => c.company_name && c.email).length})
              </Button>
            </div>
          </div>
        </div>
      )}

      {showEdit && req && (
        <EditWorkOrderModal
          req={req}
          token={token}
          onClose={() => setShowEdit(false)}
          onSaved={() => {
            setShowEdit(false);
            load();
            onChanged?.();
          }}
        />
      )}

      {showManualQuote && req && (
        <ManualQuoteModal
          requestId={id}
          contractors={contractors}
          loadContractors={async () => {
            // Fetch contractors lazily if RFQ wasn't opened yet
            if (contractors.length === 0) {
              try {
                const r = await axios.get(`${API}/api/maintenance-sourcing/${id}/preview`, { headers });
                setContractors(r.data.contractors || []);
                return r.data.contractors || [];
              } catch {
                return [];
              }
            }
            return contractors;
          }}
          token={token}
          onClose={() => setShowManualQuote(false)}
          onSaved={() => {
            setShowManualQuote(false);
            load();
            onChanged?.();
          }}
        />
      )}

      {showHospitalQuote && req && (
        <HospitalQuoteModal
          requestId={id}
          token={token}
          onClose={() => setShowHospitalQuote(false)}
          onSent={() => {
            setShowHospitalQuote(false);
            load();
            onChanged?.();
          }}
        />
      )}
    </div>
  );
}

// ============================================================================
// Edit Work Order Modal
// ============================================================================
function EditWorkOrderModal({ req, token, onClose, onSaved }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [status, setStatus] = useState(req.status || req.stage || "new");
  const [priority, setPriority] = useState(req.priority || "normal");
  const [notes, setNotes] = useState(req.notes || "");
  const [items, setItems] = useState(
    req.items?.length > 0
      ? req.items.map((i) => ({ ...i }))
      : [{
          request_type: req.request_type || "",
          description: req.description || "",
          location_details: req.location_details || req.location || "",
          notes: "",
        }],
  );
  const [saving, setSaving] = useState(false);

  // Hospital contacts — same canonical multi-contact list used by the
  // Quote Hospital flow. Inline edit so admin doesn't have to leave the
  // slide-out panel to update who gets the quote.
  const [contacts, setContacts] = useState([]);
  const [contactForm, setContactForm] = useState({ email: "", name: "", title: "" });
  const [savingContact, setSavingContact] = useState(false);

  const loadContacts = useCallback(async () => {
    try {
      const r = await axios.get(
        `${API}/api/admin/maintenance-requests/${req.id}/hospital-contacts`,
        { headers },
      );
      setContacts(r.data.contacts || []);
    } catch (e) {
      setContacts([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [req.id, token]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  const addContact = async () => {
    const email = (contactForm.email || "").trim();
    if (!email || !email.includes("@")) {
      toast.error("Valid email required");
      return;
    }
    setSavingContact(true);
    try {
      await axios.post(
        `${API}/api/admin/maintenance-requests/${req.id}/hospital-contacts`,
        {
          email,
          name: contactForm.name.trim() || undefined,
          title: contactForm.title.trim() || undefined,
          role: "additional",
          source: "manual",
        },
        { headers },
      );
      toast.success("Contact added — they'll be CC'd on the quote and updates");
      setContactForm({ email: "", name: "", title: "" });
      loadContacts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add contact");
    } finally {
      setSavingContact(false);
    }
  };

  const removeContact = async (c) => {
    if (c.implicit) {
      toast.info("Submitter contact comes from the WO itself — cannot remove from here.");
      return;
    }
    if (!window.confirm(`Remove ${c.email}?`)) return;
    try {
      await axios.delete(
        `${API}/api/admin/maintenance-requests/${req.id}/hospital-contacts/${c.id}`,
        { headers },
      );
      toast.success("Removed");
      loadContacts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove");
    }
  };

  const updateItem = (idx, field, value) => {
    setItems((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };
  const addItem = () =>
    setItems((prev) => [...prev, { request_type: "", description: "", location_details: "", notes: "" }]);
  const removeItem = (idx) =>
    setItems((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(
        `${API}/api/admin/work-orders/${req.id}`,
        {
          status,
          stage: status,
          priority,
          notes,
          items: items.filter((i) => (i.request_type || i.description)),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Work order updated");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="wo-edit-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 h-14 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <Pencil className="w-4 h-4 text-blue-600" /> Edit Work Order — {req.id}
          </h3>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Status / Stage</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="mt-1" data-testid="wo-edit-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STAGE_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger className="mt-1" data-testid="wo-edit-priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map((p) => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs">Admin notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1" data-testid="wo-edit-notes" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs">Issues</Label>
              <Button size="sm" variant="outline" onClick={addItem} data-testid="wo-edit-add-item">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add item
              </Button>
            </div>
            <div className="space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`wo-edit-item-${idx}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold text-slate-500">Issue {idx + 1}</span>
                    {items.length > 1 && (
                      <button
                        onClick={() => removeItem(idx)}
                        className="text-rose-500 hover:bg-rose-50 p-1 rounded"
                        data-testid={`wo-edit-remove-item-${idx}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    <Select value={it.request_type || ""} onValueChange={(v) => updateItem(idx, "request_type", v)}>
                      <SelectTrigger><SelectValue placeholder="Type" /></SelectTrigger>
                      <SelectContent>
                        {ISSUE_TYPES.map((t) => (
                          <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      value={it.location_details || ""}
                      onChange={(e) => updateItem(idx, "location_details", e.target.value)}
                      placeholder="Room / location"
                    />
                  </div>
                  <Textarea
                    value={it.description || ""}
                    onChange={(e) => updateItem(idx, "description", e.target.value)}
                    rows={2}
                    placeholder="Description"
                  />
                  <Input
                    value={it.notes || ""}
                    onChange={(e) => updateItem(idx, "notes", e.target.value)}
                    placeholder="Notes (optional)"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Hospital contacts editor — inline so admin doesn't have to leave the modal */}
          <div className="border border-blue-200 bg-blue-50/40 rounded-lg p-3" data-testid="wo-edit-contacts-panel">
            <div className="flex items-center gap-2 mb-2">
              <Mail className="w-4 h-4 text-blue-600" />
              <Label className="text-sm font-semibold">Hospital contacts</Label>
              <span className="text-[11px] text-slate-500">
                Everyone here is auto-CC'd on quotes and updates.
              </span>
            </div>
            {contacts.length > 0 && (
              <ul className="space-y-1 mb-3 max-h-40 overflow-y-auto pr-1">
                {contacts.map((c) => (
                  <li
                    key={c.id}
                    className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-2 py-1.5"
                    data-testid={`wo-edit-contact-row-${c.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-xs font-medium text-slate-900 truncate">
                          {c.name || c.email}
                        </span>
                        {c.role === "submitter" && (
                          <span className="text-[9px] uppercase font-bold tracking-wider px-1 py-0.5 rounded bg-emerald-100 text-emerald-700">
                            Submitter
                          </span>
                        )}
                        {c.title && <span className="text-[10px] text-slate-500">· {c.title}</span>}
                      </div>
                      {c.name && (
                        <div className="text-[11px] text-slate-500 truncate">{c.email}</div>
                      )}
                    </div>
                    {!c.implicit && (
                      <button
                        type="button"
                        onClick={() => removeContact(c)}
                        className="text-slate-300 hover:text-rose-500"
                        data-testid={`wo-edit-contact-remove-${c.id}`}
                        title="Remove"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
              <div className="sm:col-span-2">
                <Label className="text-[10px] uppercase font-bold text-slate-500">Email *</Label>
                <Input
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  placeholder="contact@hospital.com"
                  className="h-8"
                  data-testid="wo-edit-contact-email"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase font-bold text-slate-500">Name</Label>
                <Input
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  placeholder="Jane Smith"
                  className="h-8"
                />
              </div>
              <Button
                size="sm"
                type="button"
                onClick={addContact}
                disabled={savingContact}
                className="bg-blue-600 hover:bg-blue-700 h-8"
                data-testid="wo-edit-contact-add"
              >
                {savingContact ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
                Add
              </Button>
            </div>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 gap-1.5" data-testid="wo-edit-save-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save changes
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Manual Quote Entry Modal
// ============================================================================
function ManualQuoteModal({ requestId, contractors: initialContractors, loadContractors, token, onClose, onSaved }) {
  const [contractors, setContractors] = useState(initialContractors || []);
  const [pickedId, setPickedId] = useState(""); // "" = none, "__other__" = free-text
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [contact, setContact] = useState("");
  const [phone, setPhone] = useState("");
  const [labor, setLabor] = useState("");
  const [parts, setParts] = useState("");
  const [total, setTotal] = useState("");
  const [leadTime, setLeadTime] = useState("");
  const [earliest, setEarliest] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (initialContractors.length === 0 && loadContractors) {
        const fetched = await loadContractors();
        if (!cancelled) setContractors(fetched);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recompute total whenever labor/parts change
  const onMoneyChange = (which, val) => {
    if (which === "labor") setLabor(val);
    else setParts(val);
    const l = which === "labor" ? Number(val || 0) : Number(labor || 0);
    const p = which === "parts" ? Number(val || 0) : Number(parts || 0);
    if (!Number.isNaN(l + p) && (l || p)) setTotal(String(l + p));
  };

  const isOther = pickedId === "__other__";

  const save = async () => {
    if (!pickedId) {
      toast.error("Pick a contractor or choose 'Other / not in directory'");
      return;
    }
    if (isOther && (!name.trim() || !email.trim())) {
      toast.error("Contractor name + email are required");
      return;
    }
    if (!total && !labor && !parts) {
      toast.error("Enter at least a total estimate or labor/parts");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        contractor_id: isOther ? null : pickedId,
        contractor_name: isOther ? name.trim() : undefined,
        contractor_email: isOther ? email.trim() : undefined,
        contractor_contact: contact.trim() || undefined,
        contractor_phone: phone.trim() || undefined,
        labor_total: labor ? Number(labor) : undefined,
        parts_total: parts ? Number(parts) : undefined,
        total_estimate: total ? Number(total) : undefined,
        lead_time: leadTime.trim() || undefined,
        earliest_start: earliest || undefined,
        notes: notes.trim() || undefined,
      };
      await axios.post(
        `${API}/api/admin/maintenance-requests/${requestId}/contractor-quotes`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Quote logged");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="wo-manual-quote-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 h-14 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <UserPlus className="w-4 h-4 text-amber-600" /> Log Contractor Quote — {requestId}
          </h3>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">
          <div>
            <Label className="text-xs">Contractor *</Label>
            <Select value={pickedId} onValueChange={(v) => {
              setPickedId(v);
              if (v && v !== "__other__") {
                const c = contractors.find((x) => x.id === v);
                if (c) {
                  setName(c.name || "");
                  setEmail(c.email || "");
                  setContact(c.contact_name || "");
                }
              } else if (v === "__other__") {
                setName("");
                setEmail("");
                setContact("");
              }
            }}>
              <SelectTrigger className="mt-1" data-testid="wo-mq-contractor-picker">
                <SelectValue placeholder="Pick from directory…" />
              </SelectTrigger>
              <SelectContent>
                {contractors.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name} <span className="text-slate-400 text-xs">— {c.email}</span>
                  </SelectItem>
                ))}
                <SelectItem value="__other__">+ Other / not in directory</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isOther && (
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Company name *</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="wo-mq-company" />
              </div>
              <div>
                <Label className="text-xs">Email *</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="wo-mq-email" />
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Contact name</Label>
              <Input value={contact} onChange={(e) => setContact(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Phone</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-3 pt-2 border-t border-slate-100">
            <div>
              <Label className="text-xs">Labor ($)</Label>
              <Input type="number" min="0" step="0.01" value={labor} onChange={(e) => onMoneyChange("labor", e.target.value)} data-testid="wo-mq-labor" />
            </div>
            <div>
              <Label className="text-xs">Parts ($)</Label>
              <Input type="number" min="0" step="0.01" value={parts} onChange={(e) => onMoneyChange("parts", e.target.value)} data-testid="wo-mq-parts" />
            </div>
            <div>
              <Label className="text-xs">Total ($) *</Label>
              <Input type="number" min="0" step="0.01" value={total} onChange={(e) => setTotal(e.target.value)} data-testid="wo-mq-total" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Lead time</Label>
              <Input value={leadTime} onChange={(e) => setLeadTime(e.target.value)} placeholder="3-5 business days" />
            </div>
            <div>
              <Label className="text-xs">Earliest start</Label>
              <Input type="date" value={earliest} onChange={(e) => setEarliest(e.target.value)} />
            </div>
          </div>

          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="e.g. COI on file, can dispatch Monday, exclusions…" />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-amber-600 hover:bg-amber-700 gap-1.5" data-testid="wo-mq-save-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save quote
          </Button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">{title}</h3>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function KV({ label, value, icon: Icon }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-2 text-sm">
      {Icon && <Icon className="w-3.5 h-3.5 text-slate-400" />}
      <span className="text-slate-500 w-20 text-xs">{label}</span>
      <span className="text-slate-900">{value}</span>
    </div>
  );
}

// ============================================================================
// Quote Hospital Modal
// ============================================================================
function HospitalQuoteModal({ requestId, token, onClose, onSent }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [quoteNumber, setQuoteNumber] = useState("");
  const [markupMode, setMarkupMode] = useState("percent"); // percent | dollar | per_contractor
  const [markupPercent, setMarkupPercent] = useState(25);
  const [markupAmount, setMarkupAmount] = useState(0);
  const [contractorMarkups, setContractorMarkups] = useState({}); // {contractor_name: pct}
  const [freightOverride, setFreightOverride] = useState("");
  const [terms, setTerms] = useState("Net 30");
  const [validUntil, setValidUntil] = useState("30 days from issue");
  const [poInstructions, setPoInstructions] = useState(
    "To approve this work, reply to this email with PO# and authorization, or email orders@sterilemate.com referencing this quote number. Work begins once we have written approval.",
  );
  const [items, setItems] = useState([]);
  const [additionalCosts, setAdditionalCosts] = useState([]); // [{label, amount, notes, category}]
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  // Recipients editor — list of hospital_contacts that will receive the quote.
  // Pre-populated from the WO's hospital_contacts + submitter email. Admin can
  // toggle individuals off OR add ad-hoc emails just for THIS send.
  const [recipients, setRecipients] = useState([]); // [{email, name, role, source, included}]
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");

  const refreshRecipients = useCallback(async () => {
    try {
      const r = await axios.get(
        `${API}/api/admin/maintenance-requests/${requestId}/hospital-contacts`,
        { headers },
      );
      const list = (r.data.contacts || []).map((c) => ({
        email: (c.email || "").trim(),
        name: c.name,
        role: c.role,
        source: "hospital_contacts",
        included: true,
      })).filter((c) => c.email);
      setRecipients(list);
    } catch (e) {
      // non-fatal — leave list empty
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId, token]);

  useEffect(() => {
    refreshRecipients();
  }, [refreshRecipients]);

  const addAdhocRecipient = () => {
    const email = (newEmail || "").trim();
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    if (recipients.some((r) => r.email.toLowerCase() === email.toLowerCase())) {
      toast.info("Already on the list");
      return;
    }
    setRecipients((prev) => [
      ...prev,
      { email, name: newName.trim() || null, role: "adhoc", source: "adhoc", included: true },
    ]);
    setNewEmail("");
    setNewName("");
  };

  const toggleRecipient = (email) =>
    setRecipients((prev) =>
      prev.map((r) =>
        r.email === email ? { ...r, included: !r.included } : r,
      ),
    );

  const removeRecipient = (email) =>
    setRecipients((prev) => prev.filter((r) => r.email !== email));

  // Recipients ordered for the send call: TO = first included; CC = rest included
  const buildRecipientSplit = () => {
    const included = recipients.filter((r) => r.included);
    if (included.length === 0) return { to: null, cc: [] };
    // Prefer submitter as TO, then anyone else
    const submitter = included.find((r) => r.role === "submitter");
    const to = submitter || included[0];
    const cc = included.filter((r) => r.email.toLowerCase() !== to.email.toLowerCase()).map((r) => r.email);
    return { to: to.email, cc };
  };

  // Load draft + auto-suggest
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/admin/work-orders/${requestId}/customer-quote`, { headers });
        setInfo(r.data);
        const draft = r.data.draft;
        if (draft?.line_items?.length > 0) {
          setQuoteNumber(draft.quote_number || "");
          setMarkupMode(draft.markup_mode || "percent");
          setMarkupPercent(draft.markup_percent ?? 25);
          setMarkupAmount(draft.markup_amount ?? 0);
          setFreightOverride(draft.freight_override ?? "");
          setTerms(draft.terms || "Net 30");
          setValidUntil(draft.valid_until || "30 days from issue");
          setPoInstructions(draft.po_instructions || poInstructions);
          setItems(draft.line_items);
          setAdditionalCosts(draft.additional_costs || []);
          setContractorMarkups(draft.contractor_markups || {});
        } else {
          // Seed from auto_suggest
          setItems(r.data.auto_suggest?.line_items || []);
          setMarkupPercent(r.data.auto_suggest?.suggested_markup_percent || 25);
          setAdditionalCosts([]);
        }
      } catch (e) {
        toast.error("Failed to load quote draft");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  // Live preview totals
  const totals = useMemo(() => {
    const contractor_cost = items.reduce(
      (acc, it) => acc + (Number(it.contractor_cost) || 0) * (Number(it.quantity) || 1),
      0,
    );
    let lineSubtotal;
    if (markupMode === "percent") {
      lineSubtotal = contractor_cost * (1 + (Number(markupPercent) || 0) / 100);
    } else if (markupMode === "per_contractor") {
      // Each line's unit_price is already kept in sync with its contractor's
      // markup % below; just sum the resulting subtotal here.
      lineSubtotal = items.reduce(
        (acc, it) => acc + (Number(it.unit_price) || 0) * (Number(it.quantity) || 1),
        0,
      );
    } else {
      lineSubtotal = contractor_cost + (Number(markupAmount) || 0);
    }
    const freight = freightOverride !== "" ? Number(freightOverride) || 0 : 0;
    const additional_total = additionalCosts.reduce(
      (acc, c) => acc + (Number(c.amount) || 0),
      0,
    );
    return {
      contractor_cost: Math.round(contractor_cost * 100) / 100,
      subtotal: Math.round(lineSubtotal * 100) / 100,
      freight: Math.round(freight * 100) / 100,
      additional_total: Math.round(additional_total * 100) / 100,
      total: Math.round((lineSubtotal + freight + additional_total) * 100) / 100,
      implied_markup: Math.round((lineSubtotal - contractor_cost) * 100) / 100,
    };
  }, [items, markupMode, markupPercent, markupAmount, freightOverride, additionalCosts]);

  const updateItem = (idx, field, value) => {
    setItems((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  // Re-price every line sourced from a given contractor when its markup % changes
  const setContractorMarkup = (contractorName, pct) => {
    const safe = Number(pct);
    setContractorMarkups((prev) => ({ ...prev, [contractorName]: safe }));
    setItems((prev) =>
      prev.map((it) =>
        (it.source_contractor_name || "").trim() === contractorName.trim()
          ? {
              ...it,
              unit_price: Math.round(((Number(it.contractor_cost) || 0) * (1 + (safe || 0) / 100)) * 100) / 100,
            }
          : it,
      ),
    );
  };

  // Group contractor costs (used for the per-contractor markup grid)
  const contractorBreakdown = useMemo(() => {
    const byName = new Map();
    for (const it of items) {
      const name = (it.source_contractor_name || "").trim();
      if (!name) continue;
      const cur = byName.get(name) || { name, contractor_cost: 0, line_count: 0 };
      cur.contractor_cost += (Number(it.contractor_cost) || 0) * (Number(it.quantity) || 1);
      cur.line_count += 1;
      byName.set(name, cur);
    }
    return Array.from(byName.values());
  }, [items]);


  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));
  const addItem = () =>
    setItems((prev) => [...prev, { name: "", quantity: 1, contractor_cost: 0, unit_price: 0, lead_time: "", notes: "" }]);

  const ADD_COST_PRESETS = [
    { category: "after_hours", label: "After-hours surcharge" },
    { category: "permit", label: "Permit fee" },
    { category: "disposal", label: "Disposal / removal fee" },
    { category: "travel", label: "Travel / per-diem" },
    { category: "rental", label: "Equipment rental" },
    { category: "custom", label: "Custom charge" },
  ];
  const addCost = (preset) =>
    setAdditionalCosts((prev) => [
      ...prev,
      { category: preset.category, label: preset.label, amount: 0, notes: "" },
    ]);
  const updateCost = (idx, field, value) =>
    setAdditionalCosts((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  const removeCost = (idx) => setAdditionalCosts((prev) => prev.filter((_, i) => i !== idx));

  const buildPayload = () => ({
    quote_number: quoteNumber || undefined,
    markup_mode: markupMode,
    markup_percent: Number(markupPercent) || 0,
    markup_amount: Number(markupAmount) || 0,
    contractor_markups: contractorMarkups,
    freight_percent: 0,
    freight_override: freightOverride === "" ? null : Number(freightOverride) || 0,
    terms,
    valid_until: validUntil || null,
    po_instructions: poInstructions,
    line_items: items.map((it) => ({
      name: it.name,
      quantity: Number(it.quantity) || 1,
      unit_price: Number(it.unit_price) || 0,
      contractor_cost: Number(it.contractor_cost) || 0,
      lead_time: it.lead_time || null,
      notes: it.notes || null,
      source_contractor_name: it.source_contractor_name || null,
    })),
    additional_costs: additionalCosts.map((c) => ({
      label: c.label || "Additional charge",
      amount: Number(c.amount) || 0,
      notes: c.notes || null,
      category: c.category || "custom",
    })),
  });

  const save = async () => {
    if (items.length === 0) {
      toast.error("Add at least one line item");
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/api/admin/work-orders/${requestId}/customer-quote`, buildPayload(), { headers });
      toast.success("Draft saved");
      setQuoteNumber(r.data.quote_number);
      return r.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async () => {
    const ok = await save();
    if (!ok) return;
    const url = `${API}/api/admin/work-orders/${requestId}/customer-quote/pdf`;
    try {
      const r = await axios.get(url, { headers, responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `${ok.quote_number || "WO-quote"}.pdf`;
      a.click();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      toast.error("PDF generation failed");
    }
  };

  const sendQuote = async () => {
    const ok = await save();
    if (!ok) return;
    const { to, cc } = buildRecipientSplit();
    if (!to) {
      toast.error("Pick at least one recipient before sending");
      return;
    }
    setSending(true);
    try {
      const r = await axios.post(
        `${API}/api/admin/work-orders/${requestId}/customer-quote/send`,
        { attach_pdf: true, to, cc },
        { headers },
      );
      if (r.data.email_sent) {
        const ccCount = (r.data.cc || []).length;
        toast.success(
          ccCount > 0
            ? `Quote emailed to ${r.data.to} + ${ccCount} hospital contact${ccCount === 1 ? "" : "s"} CC'd`
            : `Quote emailed to ${r.data.to}`,
        );
      } else {
        toast.warning("Email send failed — opening Outlook draft as a fallback");
      }
      if (r.data.mailto_fallback) {
        window.open(r.data.mailto_fallback, "_blank");
      }
      onSent?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  // Send via Outlook (PDF auto-attached via Microsoft Graph) — calls the new
  // /outlook-draft endpoint which creates a real Outlook draft with the PDF
  // already attached and returns a webLink that opens straight into the
  // Outlook compose window. Falls back to the legacy local-download +
  // mailto: workflow when Graph permission isn't granted.
  const sendViaOutlook = async () => {
    const ok = await save();
    if (!ok) return;
    const { to, cc } = buildRecipientSplit();
    if (!to) {
      toast.error("Pick at least one recipient before sending");
      return;
    }
    setSending(true);
    try {
      const r = await axios.post(
        `${API}/api/admin/work-orders/${requestId}/customer-quote/outlook-draft`,
        { to, cc },
        { headers },
      );

      if (r.data?.status === "success" && r.data.web_link) {
        // Open the draft in Outlook web — PDF is already attached server-side
        window.open(r.data.web_link, "_blank", "noopener,noreferrer");
        toast.success(
          "Outlook draft created — PDF is attached and ready to send.",
          { duration: 6000 },
        );
        onSent?.();
        return;
      }

      // Fallback path: Graph wasn't configured, permission was missing, or the
      // API returned an error. Download the PDF locally + open a mailto:
      // compose window so admin can attach manually.
      toast.warning(
        r.data?.status === "skipped"
          ? "Microsoft Graph isn't connected — downloading PDF + opening Outlook draft"
          : "Couldn't auto-attach PDF — downloading it now so you can drag it in",
        { duration: 6000 },
      );

      const pdfRes = await axios.get(
        `${API}/api/admin/work-orders/${requestId}/customer-quote/pdf`,
        { headers, responseType: "blob" },
      );
      const fname = `Quote_${requestId}.pdf`;
      const blobUrl = window.URL.createObjectURL(pdfRes.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);

      if (r.data?.mailto_fallback) {
        window.location.href = r.data.mailto_fallback;
      }
      onSent?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Outlook draft failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="wo-hospital-quote-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-start justify-between px-5 pt-3 pb-2 border-b border-slate-200 bg-gradient-to-r from-emerald-50 to-white">
          <div className="flex-1">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Calculator className="w-4 h-4 text-emerald-600" /> Quote Hospital — {requestId}
            </h3>
            {/* Awarded contractor chips — shows ALL when multi-award */}
            {(info?.auto_suggest?.awarded_contractors?.length || 0) > 0 && (
              <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                  Awarded:
                </span>
                {info.auto_suggest.awarded_contractors.map((c, i) => (
                  <span
                    key={c.id || i}
                    className="inline-flex items-center gap-1 bg-violet-100 text-violet-800 border border-violet-200 rounded-full px-2 py-0.5 text-[11px] font-medium"
                    data-testid={`wo-hq-awarded-${i}`}
                    title={c.notes || ""}
                  >
                    {c.name} · ${Number(c.amount || 0).toLocaleString()}
                  </span>
                ))}
                {info.auto_suggest.multi_award && (
                  <span className="text-[10px] text-violet-700 font-semibold ml-1">
                    Both will appear on the hospital quote
                  </span>
                )}
              </div>
            )}
            {!info?.auto_suggest?.awarded_contractors?.length && info?.auto_suggest?.contractor_name && (
              <p className="text-xs text-slate-500 mt-0.5">
                Based on {info.auto_suggest.contractor_name} (${(info.auto_suggest.contractor_total || 0).toLocaleString()})
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {/* Markup controls */}
              <div className="bg-emerald-50/60 border border-emerald-200 rounded-xl p-4">
                <Label className="text-xs font-bold uppercase tracking-wider text-emerald-700">
                  Profit Margin
                </Label>
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <div className="flex bg-white border border-emerald-200 rounded-lg overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setMarkupMode("percent")}
                      className={`px-3 py-1.5 text-sm font-medium ${markupMode === "percent" ? "bg-emerald-600 text-white" : "text-slate-700"}`}
                      data-testid="wo-hq-mode-percent"
                    >
                      %
                    </button>
                    <button
                      type="button"
                      onClick={() => setMarkupMode("dollar")}
                      className={`px-3 py-1.5 text-sm font-medium ${markupMode === "dollar" ? "bg-emerald-600 text-white" : "text-slate-700"}`}
                      data-testid="wo-hq-mode-dollar"
                    >
                      $
                    </button>
                    {contractorBreakdown.length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          setMarkupMode("per_contractor");
                          // Seed any missing contractor markups @ 25% and reprice their lines
                          const next = { ...contractorMarkups };
                          let changed = false;
                          contractorBreakdown.forEach((c) => {
                            if (next[c.name] === undefined || next[c.name] === null) {
                              next[c.name] = 25;
                              changed = true;
                            }
                          });
                          if (changed) setContractorMarkups(next);
                          // Reprice all lines from their contractor's pct (or 25 default)
                          setItems((prev) =>
                            prev.map((it) => {
                              const cname = (it.source_contractor_name || "").trim();
                              if (!cname) return it;
                              const pct = Number(next[cname] ?? 25);
                              const cost = Number(it.contractor_cost) || 0;
                              return {
                                ...it,
                                unit_price: Math.round(cost * (1 + pct / 100) * 100) / 100,
                              };
                            }),
                          );
                        }}
                        className={`px-3 py-1.5 text-sm font-medium border-l border-emerald-200 ${markupMode === "per_contractor" ? "bg-violet-600 text-white" : "text-slate-700"}`}
                        data-testid="wo-hq-mode-per-contractor"
                        title="Tune margin separately for each awarded contractor"
                      >
                        Per contractor
                      </button>
                    )}
                  </div>
                  {markupMode === "percent" ? (
                    <div className="flex items-center gap-1">
                      <Input
                        type="number"
                        min="0"
                        step="0.5"
                        value={markupPercent}
                        onChange={(e) => setMarkupPercent(e.target.value)}
                        className="w-24"
                        data-testid="wo-hq-markup-pct"
                      />
                      <span className="text-slate-600 text-sm">%</span>
                    </div>
                  ) : markupMode === "dollar" ? (
                    <div className="flex items-center gap-1">
                      <span className="text-slate-600 text-sm">$</span>
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        value={markupAmount}
                        onChange={(e) => setMarkupAmount(e.target.value)}
                        className="w-32"
                        data-testid="wo-hq-markup-amt"
                      />
                    </div>
                  ) : null}
                  <span className="text-xs text-slate-500">
                    Contractor cost: ${totals.contractor_cost.toLocaleString()} → markup ${totals.implied_markup.toLocaleString()}
                  </span>
                </div>
                <p className="text-[11px] text-slate-500 mt-2">
                  Markup is applied to each line item's contractor_cost and recomputed live in the line totals below.
                </p>

                {/* Per-contractor markup grid — only when multi-award + that mode */}
                {markupMode === "per_contractor" && contractorBreakdown.length > 1 && (
                  <div className="mt-3 rounded-lg border border-violet-200 bg-violet-50/60 p-3" data-testid="wo-hq-per-contractor-grid">
                    <p className="text-[11px] text-violet-700 font-semibold mb-2">
                      Set a separate markup % for each awarded contractor — line items below auto-reprice as you type.
                    </p>
                    <table className="w-full text-sm">
                      <thead className="text-[10px] font-bold uppercase tracking-wider text-violet-700">
                        <tr>
                          <th className="text-left pb-1">Contractor</th>
                          <th className="text-right pb-1 w-24">Cost</th>
                          <th className="text-center pb-1 w-24">Markup %</th>
                          <th className="text-right pb-1 w-28">Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contractorBreakdown.map((c) => {
                          const pct = Number(contractorMarkups[c.name] ?? 25);
                          const sub = c.contractor_cost * (1 + pct / 100);
                          return (
                            <tr key={c.name} data-testid={`wo-hq-cmarkup-${c.name.replace(/\s+/g, '-')}`}>
                              <td className="py-1.5 text-slate-800 font-medium">
                                {c.name}
                                <span className="ml-2 text-[10px] text-slate-500">{c.line_count} line{c.line_count !== 1 ? 's' : ''}</span>
                              </td>
                              <td className="py-1.5 text-right text-slate-700 font-mono text-xs">
                                ${c.contractor_cost.toLocaleString()}
                              </td>
                              <td className="py-1.5">
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.5"
                                  value={contractorMarkups[c.name] ?? 25}
                                  onChange={(e) => setContractorMarkup(c.name, e.target.value)}
                                  className="h-8 w-20 mx-auto text-center"
                                />
                              </td>
                              <td className="py-1.5 text-right text-emerald-700 font-semibold font-mono">
                                ${sub.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Line items */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Line items</Label>
                  <Button size="sm" variant="outline" onClick={addItem} data-testid="wo-hq-add-item">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add line
                  </Button>
                </div>
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="px-3 py-2 text-left">Service</th>
                        <th className="px-3 py-2 text-center w-14">Qty</th>
                        <th className="px-3 py-2 text-right w-28">Cost</th>
                        <th className="px-3 py-2 text-right w-28">Customer $</th>
                        <th className="px-3 py-2 text-right w-28">Line total</th>
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {items.length === 0 ? (
                        <tr><td colSpan={6} className="px-3 py-4 text-center text-slate-400 text-sm">No line items yet — add one or seed from a contractor quote.</td></tr>
                      ) : items.map((it, idx) => {
                        const cost = Number(it.contractor_cost) || 0;
                        const qty = Number(it.quantity) || 1;
                        const marked =
                          markupMode === "percent"
                            ? cost * (1 + (Number(markupPercent) || 0) / 100)
                            : markupMode === "per_contractor"
                            ? Number(it.unit_price) || 0
                            : (() => {
                                const total_cost = items.reduce((a, b) => a + (Number(b.contractor_cost) || 0) * (Number(b.quantity) || 1), 0);
                                const share = total_cost > 0 ? (cost * qty) / total_cost : 1 / Math.max(items.length, 1);
                                return cost + ((Number(markupAmount) || 0) * share) / qty;
                              })();
                        const lineTotal = marked * qty;
                        return (
                          <tr key={idx}>
                            <td className="px-3 py-2">
                              <Input
                                value={it.name || ""}
                                onChange={(e) => updateItem(idx, "name", e.target.value)}
                                className="h-8"
                                placeholder="Service description"
                              />
                              <Input
                                value={it.notes || ""}
                                onChange={(e) => updateItem(idx, "notes", e.target.value)}
                                className="h-7 mt-1 text-xs"
                                placeholder="Notes (optional)"
                              />
                              {it.source_contractor_name && (
                                <span className="inline-block text-[10px] font-medium bg-violet-50 text-violet-700 border border-violet-200 rounded-full px-2 py-0.5 mt-1">
                                  from {it.source_contractor_name}
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <Input
                                type="number"
                                min="1"
                                value={it.quantity}
                                onChange={(e) => updateItem(idx, "quantity", e.target.value)}
                                className="h-8 text-center"
                              />
                            </td>
                            <td className="px-3 py-2 text-right">
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={it.contractor_cost || 0}
                                onChange={(e) => updateItem(idx, "contractor_cost", e.target.value)}
                                className="h-8 text-right"
                              />
                            </td>
                            <td className="px-3 py-2 text-right font-semibold text-emerald-700">
                              ${marked.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-3 py-2 text-right font-semibold text-slate-900">
                              ${lineTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <button
                                onClick={() => removeItem(idx)}
                                className="text-rose-500 hover:bg-rose-50 p-1 rounded"
                                aria-label="Remove line"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Additional Costs — ad-hoc charges (permits, after-hours, etc.) */}
              <div>
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Additional Costs
                  </Label>
                  <div className="flex gap-1 flex-wrap">
                    {ADD_COST_PRESETS.map((preset) => (
                      <Button
                        key={preset.category}
                        size="sm"
                        variant="outline"
                        onClick={() => addCost(preset)}
                        className="h-7 px-2 text-[11px]"
                        data-testid={`wo-hq-add-cost-${preset.category}`}
                      >
                        <Plus className="w-2.5 h-2.5 mr-1" /> {preset.label}
                      </Button>
                    ))}
                  </div>
                </div>
                {additionalCosts.length > 0 ? (
                  <div className="border border-amber-200 bg-amber-50/40 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-amber-100/60 text-[10px] font-bold uppercase tracking-wider text-amber-800">
                        <tr>
                          <th className="px-3 py-2 text-left">Charge</th>
                          <th className="px-3 py-2 text-right w-32">Amount</th>
                          <th className="w-8"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-amber-100">
                        {additionalCosts.map((c, idx) => (
                          <tr key={idx} data-testid={`wo-hq-cost-row-${idx}`}>
                            <td className="px-3 py-2">
                              <Input
                                value={c.label || ""}
                                onChange={(e) => updateCost(idx, "label", e.target.value)}
                                className="h-8"
                                placeholder="e.g. After-hours surcharge"
                              />
                              <Input
                                value={c.notes || ""}
                                onChange={(e) => updateCost(idx, "notes", e.target.value)}
                                className="h-7 mt-1 text-xs"
                                placeholder="Notes (optional, visible to hospital)"
                              />
                            </td>
                            <td className="px-3 py-2 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <span className="text-slate-600">$</span>
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={c.amount || 0}
                                  onChange={(e) => updateCost(idx, "amount", e.target.value)}
                                  className="h-8 w-28 text-right"
                                />
                              </div>
                            </td>
                            <td className="px-3 py-2 text-center">
                              <button
                                onClick={() => removeCost(idx)}
                                className="text-rose-500 hover:bg-rose-50 p-1 rounded"
                                aria-label="Remove charge"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-[11px] text-slate-500 italic">
                    Add ad-hoc charges that aren&apos;t from a contractor line (permits, after-hours, disposal, etc.). These appear as their own section on the hospital&apos;s quote.
                  </p>
                )}
              </div>

              {/* Terms + freight + PO */}
              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Terms</Label>
                  <Input value={terms} onChange={(e) => setTerms(e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">Valid until</Label>
                  <Input value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">Travel / freight ($)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={freightOverride}
                    onChange={(e) => setFreightOverride(e.target.value)}
                    placeholder="0 (none)"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">How to approve (PO instructions)</Label>
                <Textarea
                  rows={2}
                  value={poInstructions}
                  onChange={(e) => setPoInstructions(e.target.value)}
                />
              </div>

              {/* Recipients editor — who gets the quote on this send */}
              <div className="bg-blue-50/40 border border-blue-200 rounded-xl p-4" data-testid="wo-hq-recipients">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-blue-600" />
                    <span className="font-semibold text-slate-800 text-sm">
                      Recipients
                    </span>
                    <span className="text-[11px] text-slate-500">
                      {recipients.filter((r) => r.included).length} included
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={refreshRecipients}
                    className="text-[11px] text-blue-600 hover:underline flex items-center gap-1"
                    data-testid="wo-hq-recipients-refresh"
                  >
                    <RefreshCw className="w-3 h-3" /> Refresh from contacts
                  </button>
                </div>
                {recipients.length === 0 ? (
                  <p className="text-xs text-slate-500 italic mb-2">
                    No hospital contacts yet. Add one below OR via the Edit Work Order panel.
                  </p>
                ) : (
                  <ul className="space-y-1 mb-2 max-h-44 overflow-y-auto pr-1">
                    {recipients.map((r, idx) => (
                      <li
                        key={r.email}
                        className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-2 py-1.5"
                        data-testid={`wo-hq-recipient-row-${idx}`}
                      >
                        <input
                          type="checkbox"
                          checked={r.included}
                          onChange={() => toggleRecipient(r.email)}
                          className="w-3.5 h-3.5"
                          data-testid={`wo-hq-recipient-toggle-${idx}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-xs font-medium text-slate-900 truncate">
                              {r.name || r.email}
                            </span>
                            {r.role === "submitter" && (
                              <span className="text-[9px] uppercase font-bold tracking-wider px-1 py-0.5 rounded bg-emerald-100 text-emerald-700">
                                Submitter / TO
                              </span>
                            )}
                            {r.role === "adhoc" && (
                              <span className="text-[9px] uppercase font-bold tracking-wider px-1 py-0.5 rounded bg-amber-100 text-amber-700">
                                Ad-hoc
                              </span>
                            )}
                          </div>
                          {r.name && (
                            <div className="text-[11px] text-slate-500 truncate">{r.email}</div>
                          )}
                        </div>
                        {r.source === "adhoc" && (
                          <button
                            type="button"
                            onClick={() => removeRecipient(r.email)}
                            className="text-slate-300 hover:text-rose-500"
                            data-testid={`wo-hq-recipient-remove-${idx}`}
                            title="Remove ad-hoc recipient"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex items-center gap-2">
                  <Input
                    type="email"
                    placeholder="name@hospital.com"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addAdhocRecipient();
                      }
                    }}
                    className="h-8 flex-1"
                    data-testid="wo-hq-recipient-new-email"
                  />
                  <Input
                    placeholder="Name (optional)"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addAdhocRecipient();
                      }
                    }}
                    className="h-8 w-44"
                  />
                  <Button
                    size="sm"
                    type="button"
                    onClick={addAdhocRecipient}
                    variant="outline"
                    className="h-8"
                    data-testid="wo-hq-recipient-add"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add
                  </Button>
                </div>
                <p className="text-[11px] text-slate-500 mt-2">
                  Ad-hoc emails added here are used <strong>just for this send</strong>. To save a contact permanently, use Edit Work Order → Hospital contacts.
                </p>
              </div>

              {/* Totals preview */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                <div className="text-xs text-slate-500">Live preview</div>
                <div className="mt-1 flex flex-wrap items-end justify-between gap-3">
                  <div>
                    <div className="text-sm text-slate-600">
                      Subtotal ${totals.subtotal.toLocaleString()}
                      {totals.additional_total > 0 && ` · Additional $${totals.additional_total.toLocaleString()}`}
                      {totals.freight > 0 && ` · Freight $${totals.freight.toLocaleString()}`}
                    </div>
                    <div className="text-3xl font-extrabold text-emerald-700 leading-none mt-1" data-testid="wo-hq-total">
                      ${totals.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">
                      Contractor cost ${totals.contractor_cost.toLocaleString()} + markup ${totals.implied_markup.toLocaleString()}
                    </div>
                  </div>
                  {info?.request && (                    <div className="text-xs text-slate-500 text-right">
                      <div>For: <strong className="text-slate-700">{info.request.hospital_name}</strong></div>
                      <div>{info.request.contact_email || "(no email on file)"}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2 flex-wrap">
              <Button variant="outline" onClick={onClose}>Close</Button>
              <Button
                variant="outline"
                onClick={save}
                disabled={saving}
                className="gap-1.5"
                data-testid="wo-hq-save-btn"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Save draft
              </Button>
              <Button
                variant="outline"
                onClick={downloadPdf}
                disabled={saving}
                className="gap-1.5"
                data-testid="wo-hq-pdf-btn"
              >
                <FileDown className="w-3.5 h-3.5" /> Download PDF
              </Button>
              <Button
                variant="outline"
                onClick={sendViaOutlook}
                disabled={saving || sending || items.length === 0}
                className="gap-1.5 border-blue-300 text-blue-700 hover:bg-blue-50"
                data-testid="wo-hq-outlook-btn"
                title="Downloads the PDF + opens an Outlook draft pre-populated with recipients. Drag the PDF into the draft."
              >
                {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                Send via Outlook
              </Button>
              <Button
                onClick={sendQuote}
                disabled={sending || items.length === 0}
                className="bg-emerald-600 hover:bg-emerald-700 gap-1.5"
                data-testid="wo-hq-send-btn"
              >
                {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Send to hospital
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Main Board
// ============================================================================
export default function AdminWorkOrdersBoard() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(
    () => new URLSearchParams(window.location.search).get("open") || null);
  const [showNew, setShowNew] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/work-orders`, {
        headers,
        params: { status: statusFilter, search: search || undefined },
      });
      setItems(r.data.items || []);
    } catch (e) {
      toast.error("Failed to load work orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const stats = useMemo(() => {
    const base = {
      all: items.length,
      new: 0,
      dispatching: 0,
      sent_to_contractors: 0,
      quotes_received: 0,
      awarded: 0,
      in_progress: 0,
      completed: 0,
    };
    items.forEach((it) => {
      const s = it.status || it.stage || "new";
      const key = ["intake", "pending"].includes(s) ? "new" : s;
      if (base[key] !== undefined) base[key] += 1;
    });
    return base;
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => {
      const hay = `${it.id || ""} ${it.hospital_name || ""} ${it.description || ""} ${it.contact_name || ""} ${it.contact_email || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, search]);

  return (
    <div className="space-y-5" data-testid="admin-work-orders-board">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Work Orders Command Center</h1>
          <p className="text-sm text-slate-500">
            All maintenance requests — hospital portal, admin entry, public form — in one place.
            Send RFQs to contractors and award winning quotes, just like backorders.
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => setShowNew(true)}
          className="bg-blue-600 hover:bg-blue-700 gap-1.5 shrink-0"
          data-testid="wo-new-btn"
        >
          <Plus className="w-4 h-4" /> New Work Order
        </Button>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
        {[
          { key: "all",                 label: "All",          icon: Wrench,       color: "bg-slate-100 text-slate-700" },
          { key: "new",                 label: "New",          icon: AlertCircle,  color: "bg-blue-50 text-blue-700" },
          { key: "dispatching",         label: "Dispatching",  icon: Users,        color: "bg-amber-50 text-amber-700" },
          { key: "sent_to_contractors", label: "RFQ Sent",     icon: Send,         color: "bg-amber-50 text-amber-700" },
          { key: "quotes_received",     label: "Quotes In",    icon: ListChecks,   color: "bg-emerald-50 text-emerald-700" },
          { key: "awarded",             label: "Awarded",      icon: Award,        color: "bg-violet-50 text-violet-700" },
          { key: "in_progress",         label: "In Progress",  icon: Clock,        color: "bg-cyan-50 text-cyan-700" },
          { key: "completed",           label: "Completed",    icon: CheckCircle2, color: "bg-slate-50 text-slate-700" },
        ].map((k) => {
          const active = statusFilter === k.key;
          const Icon = k.icon;
          return (
            <button
              key={k.key}
              onClick={() => setStatusFilter(k.key)}
              className={`text-left rounded-xl border p-3 transition-all ${
                active
                  ? "border-blue-400 ring-2 ring-blue-100"
                  : "border-slate-200 hover:border-slate-300 hover:shadow-sm"
              } bg-white`}
              data-testid={`wo-kpi-${k.key}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <div className={`w-7 h-7 rounded-md flex items-center justify-center ${k.color}`}>
                  <Icon className="w-3.5 h-3.5" />
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{k.label}</span>
              </div>
              <div className="text-2xl font-extrabold text-slate-900 leading-none mt-1">{stats[k.key] ?? 0}</div>
            </button>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search by ID, hospital, description, contact…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9"
            data-testid="wo-search"
          />
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} className="gap-1.5">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Refresh
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <tr>
                <th className="px-4 py-2 text-left">Request</th>
                <th className="px-4 py-2 text-left">Hospital · Site</th>
                <th className="px-4 py-2 text-left">Issues</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">Priority</th>
                <th className="px-4 py-2 text-left">Quotes</th>
                <th className="px-4 py-2 text-left">Age</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-500">No work orders match.</td></tr>
              )}
              {!loading && filtered.map((it) => {
                const items_count = (it.items?.length) || 1;
                return (
                  <tr
                    key={it.id}
                    className="hover:bg-slate-50 cursor-pointer"
                    onClick={() => setSelectedId(it.id)}
                    data-testid={`wo-row-${it.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-slate-700">{it.id}</span>
                        <SourceBadge source={it.source} />
                      </div>
                      {it.parts_on_backorder && (
                        <span
                          className="mt-1 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 border border-rose-300"
                          title="Waiting on backordered parts — sourcing request open"
                          data-testid={`wo-row-parts-backorder-${it.id}`}
                        >
                          <AlertCircle className="w-2.5 h-2.5" /> Parts on backorder
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-slate-900 truncate max-w-[200px]">{it.hospital_name || "—"}</p>
                      {it.facility_name && <p className="text-xs text-slate-500 truncate max-w-[200px]">{it.facility_name}</p>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-slate-700 truncate max-w-[260px]">
                        {it.items?.length > 0
                          ? it.items.map((i) => (i.request_type || "issue").replace(/_/g, " ")).join(" · ")
                          : (it.request_type || "issue").replace(/_/g, " ")
                        }
                      </div>
                      <p className="text-xs text-slate-500 truncate max-w-[260px]">
                        {items_count} item{items_count === 1 ? "" : "s"} · {it.location_details || it.location || "—"}
                      </p>
                    </td>
                    <td className="px-4 py-3"><StatusPill status={it.status || it.stage} /></td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${PRIORITY_META[it.priority] || "bg-slate-100 text-slate-600"}`}>
                        {it.priority || "normal"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {it.contractor_quote_count > 0 ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                          <ListChecks className="w-3.5 h-3.5" /> {it.contractor_quote_count}
                        </span>
                      ) : (
                        <span className="text-slate-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">{fmtAge(it.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/admin/work-orders/${it.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-violet-600 hover:text-violet-800 font-semibold text-xs flex items-center gap-1"
                          title="Open full detail page (updates, checklist, sign-off)"
                          data-testid={`wo-detail-${it.id}`}
                        >
                          Detail <ExternalLink className="w-3 h-3" />
                        </Link>
                        <button
                          className="text-blue-600 hover:text-blue-800 font-semibold text-xs flex items-center gap-1"
                          onClick={(e) => { e.stopPropagation(); setSelectedId(it.id); }}
                        >
                          Open <ChevronRight className="w-3 h-3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {selectedId && (
        <WorkOrderDrawer
          id={selectedId}
          token={token}
          onClose={() => setSelectedId(null)}
          onChanged={load}
        />
      )}

      {showNew && (
        <NewWorkOrderInline
          open={showNew}
          token={token}
          onClose={() => setShowNew(false)}
          onSuccess={() => { setShowNew(false); load(); }}
        />
      )}
    </div>
  );
}


// ============================================================================
// New Work Order modal — admin creates a maintenance request and chooses one
// of three dispatch modes:
//   (1) triage      — save only, route nowhere yet
//   (2) direct      — assign one contractor (skips RFQ; uses internal-maintenance-request)
//   (3) rfq         — broadcast RFQ to multiple contractors (mirrors backorder flow)
// ============================================================================
function NewWorkOrderInline({ open, onClose, onSuccess, token }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState("rfq"); // "triage" | "direct" | "rfq"
  const [issueType, setIssueType] = useState("equipment_malfunction");
  const [priority, setPriority] = useState("normal");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [assetId, setAssetId] = useState("");
  const [assetName, setAssetName] = useState("");
  const [notes, setNotes] = useState("");
  const [scheduledDate, setScheduledDate] = useState("");
  // Attachments — uploaded immediately on file select; stores their public URLs
  const [attachments, setAttachments] = useState([]); // [{file_url, file_name, size_bytes, content_type}]
  const [uploading, setUploading] = useState(false);

  // Hospital pick via CRM
  const [pickerOpen, setPickerOpen] = useState(false);
  const [picked, setPicked] = useState(null);
  const [hospitalName, setHospitalName] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  // Additional CC contacts (multi-pick from CRM filtered to the chosen hospital)
  const [ccPickerOpen, setCcPickerOpen] = useState(false);
  const [ccContacts, setCcContacts] = useState([]); // [{name, email}]

  // Contractor selection
  const [contractors, setContractors] = useState([]);
  const [directContractorId, setDirectContractorId] = useState("");
  const [rfqContractorIds, setRfqContractorIds] = useState(new Set());
  const [contractorSearch, setContractorSearch] = useState("");

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/api/admin/contractors/directory`, { headers })
      .then((r) => setContractors(r.data?.contractors || r.data || []))
      .catch(() => setContractors([]));
  }, [open, headers]);

  if (!open) return null;

  const filteredContractors = (() => {
    const q = contractorSearch.trim().toLowerCase();
    if (!q) return contractors;
    return contractors.filter((c) => {
      const hay = `${c.company_name || c.name || ""} ${c.contact_name || ""} ${c.email || c.contact_email || ""} ${(c.specialties || []).join(" ")}`.toLowerCase();
      return hay.includes(q);
    });
  })();

  const toggleRfq = (id) => {
    setRfqContractorIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-selecting the same file later
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > 25 * 1024 * 1024) {
          toast.error(`${file.name} exceeds 25 MB`);
          continue;
        }
        const form = new FormData();
        form.append("file", file);
        try {
          const res = await axios.post(
            `${API}/api/admin/work-orders/upload-attachment`,
            form,
            { headers: { ...headers, "Content-Type": "multipart/form-data" } }
          );
          setAttachments((prev) => [...prev, res.data]);
        } catch (err) {
          toast.error(`${file.name}: ${err.response?.data?.detail || err.message}`);
        }
      }
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const fmtBytes = (n) => {
    if (!n) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!hospitalName.trim()) { toast.error("Pick a hospital from CRM (or type a facility name)"); return; }
    if (!description.trim()) { toast.error("Describe the issue"); return; }
    if (mode === "direct" && !directContractorId) { toast.error("Pick the contractor to assign"); return; }
    if (mode === "rfq" && rfqContractorIds.size === 0) { toast.error("Select at least one contractor for the RFQ"); return; }

    setSubmitting(true);
    try {
      const createPayload = {
        hospital_id: picked?.account_id || "manual",
        hospital_name: hospitalName,
        facility_name: hospitalName,
        request_type: issueType,
        priority,
        description: description + (location ? `\n\nLocation: ${location}` : ""),
        asset_id: assetId || null,
        asset_name: assetName || null,
        contact_name: contactName || null,
        contact_email: contactEmail || null,
        contact_phone: contactPhone || null,
        scheduled_date: scheduledDate || null,
        notes: notes || null,
        cc_emails: ccContacts.map((c) => c.email).filter(Boolean),
        assigned_contractors: mode === "direct" && directContractorId ? [directContractorId] : [],
        photos: attachments.map((a) => a.file_url),
        attachments: attachments,
        force_assign: true,
      };
      const created = await axios.post(`${API}/api/admin/internal-maintenance-request`, createPayload, { headers });
      const requestId = created.data?.request_id || created.data?.id;
      if (!requestId) throw new Error("No request_id returned");

      if (mode === "rfq") {
        try {
          await axios.post(
            `${API}/api/maintenance-sourcing/${requestId}/broadcast`,
            { contractor_ids: Array.from(rfqContractorIds), new_contractors: [] },
            { headers }
          );
          toast.success(`Work order ${requestId} created — RFQ sent to ${rfqContractorIds.size} contractor${rfqContractorIds.size === 1 ? "" : "s"}`);
        } catch (broadcastErr) {
          toast.warning(`Work order ${requestId} created, but RFQ broadcast failed: ${broadcastErr.response?.data?.detail || broadcastErr.message}`);
        }
      } else if (mode === "direct") {
        const c = contractors.find((x) => x.id === directContractorId);
        toast.success(`Work order ${requestId} created — assigned to ${c?.company_name || c?.name || "contractor"}`);
      } else {
        toast.success(`Work order ${requestId} created in triage`);
      }

      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Failed to create work order");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-3xl w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="wo-new-modal">
        <div className="flex items-center justify-between px-5 h-14 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <Wrench className="w-4 h-4 text-blue-600" /> New Work Order
          </h2>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4">
          <section>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Hospital / Facility *</Label>
              <Button type="button" size="sm" variant="outline" onClick={() => setPickerOpen(true)} className="gap-1.5 h-7 text-xs" data-testid="wo-new-pick-crm">
                <Users className="w-3 h-3" /> Pick from CRM…
              </Button>
            </div>
            {picked ? (
              <div className="rounded border border-emerald-300 bg-emerald-50/60 p-2 flex items-start justify-between gap-2">
                <div className="text-sm">
                  <div className="font-bold text-slate-900">{picked.account_name}</div>
                  <div className="text-xs text-slate-600">{contactName || picked.name || ""} · {contactEmail || picked.email || ""}</div>
                </div>
                <button type="button" onClick={() => { setPicked(null); setHospitalName(""); setContactName(""); setContactEmail(""); setContactPhone(""); }} className="text-rose-500 hover:text-rose-700">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <Input
                placeholder="Hospital / facility name *"
                value={hospitalName}
                onChange={(e) => setHospitalName(e.target.value)}
                data-testid="wo-new-hospital-name"
              />
            )}
          </section>

          <section className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Issue type</Label>
              <Select value={issueType} onValueChange={setIssueType}>
                <SelectTrigger className="mt-1" data-testid="wo-new-issue-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ISSUE_TYPES.map((t) => (<SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger className="mt-1" data-testid="wo-new-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Scheduled date (optional)</Label>
              <Input type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} className="mt-1" />
            </div>
          </section>

          <section>
            <Label className="text-xs">Description *</Label>
            <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1" placeholder="What's wrong? Where? Any context for the contractor." data-testid="wo-new-description" />
          </section>

          <section className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Location / room</Label>
              <Input value={location} onChange={(e) => setLocation(e.target.value)} className="mt-1" placeholder="e.g. SPD wash room A" />
            </div>
            <div>
              <Label className="text-xs">Asset name (optional)</Label>
              <Input value={assetName} onChange={(e) => setAssetName(e.target.value)} className="mt-1" placeholder="e.g. Steris washer #2" />
            </div>
          </section>

          <section className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">On-site contact</Label>
              <Input value={contactName} onChange={(e) => setContactName(e.target.value)} className="mt-1" placeholder="Name" />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} className="mt-1" placeholder="contact@hospital.com" />
            </div>
            <div>
              <Label className="text-xs">Phone</Label>
              <Input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} className="mt-1" placeholder="713-555-0100" />
            </div>
          </section>

          {/* Additional hospital contacts to CC on the confirmation email */}
          <section>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Also notify (CC, optional)</Label>
              <Button type="button" size="sm" variant="outline" onClick={() => setCcPickerOpen(true)} className="gap-1.5 h-7 text-xs" data-testid="wo-new-add-cc">
                <Users className="w-3 h-3" /> Add hospital contact…
              </Button>
            </div>
            {ccContacts.length === 0 ? (
              <p className="text-[11px] text-slate-500 italic">No CC recipients. Add anyone else at the hospital who should get the confirmation email.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {ccContacts.map((c, i) => (
                  <span
                    key={`${c.email}-${i}`}
                    className="inline-flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded-full pl-2.5 pr-1 py-0.5 text-xs"
                    data-testid={`wo-new-cc-chip-${i}`}
                  >
                    <span className="font-semibold text-slate-800">{c.name || c.email}</span>
                    <span className="text-slate-500">{c.email}</span>
                    <button
                      type="button"
                      onClick={() => setCcContacts((prev) => prev.filter((_, idx) => idx !== i))}
                      className="rounded-full p-0.5 hover:bg-blue-100 text-blue-700"
                      title="Remove"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </section>

          <section>
            <Label className="text-xs">Admin notes (internal)</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" placeholder="Anything the contractor shouldn't see." />
          </section>

          {/* Attachments / photos */}
          <section>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Photos &amp; attachments
              </Label>
              <label
                className="inline-flex items-center gap-1.5 h-7 px-2 text-xs font-medium rounded border border-slate-300 bg-white hover:bg-slate-50 cursor-pointer"
                data-testid="wo-new-attach-btn"
              >
                {uploading ? (
                  <><Loader2 className="w-3 h-3 animate-spin" /> Uploading…</>
                ) : (
                  <><Paperclip className="w-3 h-3" /> Add files…</>
                )}
                <input
                  type="file"
                  multiple
                  accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.gif,.doc,.docx,.xls,.xlsx,.txt,.csv,.mp4,.mov"
                  className="hidden"
                  onChange={handleFileSelect}
                  disabled={uploading}
                  data-testid="wo-new-attach-input"
                />
              </label>
            </div>
            {attachments.length === 0 ? (
              <p className="text-[11px] text-slate-500 italic">
                Photos of the issue, equipment nameplates, scope documents, prior service reports. PDFs / images / Office docs up to 25 MB.
              </p>
            ) : (
              <div className="space-y-1.5">
                {attachments.map((a, i) => {
                  const isImg = /\.(png|jpe?g|webp|gif|heic)$/i.test(a.file_name || "");
                  return (
                    <div
                      key={`${a.file_url}-${i}`}
                      className="flex items-center gap-2 rounded border border-slate-200 bg-white p-1.5 pr-2"
                      data-testid={`wo-new-attach-chip-${i}`}
                    >
                      {isImg ? (
                        <img
                          src={`${API}${a.file_url}`}
                          alt={a.file_name}
                          className="w-9 h-9 object-cover rounded border border-slate-200 flex-shrink-0"
                        />
                      ) : (
                        <div className="w-9 h-9 rounded bg-slate-100 border border-slate-200 flex items-center justify-center flex-shrink-0">
                          <Paperclip className="w-4 h-4 text-slate-500" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <a
                          href={`${API}${a.file_url}`}
                          target="_blank"
                          rel="noreferrer"
                          className="block truncate text-xs font-semibold text-slate-800 hover:text-blue-600"
                          title={a.file_name}
                        >
                          {a.file_name}
                        </a>
                        <p className="text-[10px] text-slate-500">{fmtBytes(a.size_bytes)}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAttachment(i)}
                        className="rounded p-1 hover:bg-rose-50 text-rose-500 flex-shrink-0"
                        title="Remove"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <section className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3" data-testid="wo-new-mode-section">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Dispatch action</Label>
            <div className="grid sm:grid-cols-3 gap-2">
              {[
                { id: "triage", label: "Save as triage", desc: "Create only — no contractor notified yet." },
                { id: "direct", label: "Direct assign", desc: "Assign one contractor; they get an immediate notification." },
                { id: "rfq",    label: "RFQ broadcast", desc: "Send to multiple contractors; collect quotes & award." },
              ].map((m) => (
                <button
                  type="button"
                  key={m.id}
                  onClick={() => setMode(m.id)}
                  className={`text-left rounded-lg border-2 p-2.5 transition ${
                    mode === m.id ? "border-blue-500 bg-white shadow-sm" : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                  data-testid={`wo-new-mode-${m.id}`}
                >
                  <div className="text-sm font-bold text-slate-900">{m.label}</div>
                  <div className="text-[11px] text-slate-500 leading-snug mt-0.5">{m.desc}</div>
                </button>
              ))}
            </div>

            {mode === "direct" && (
              <div>
                <Label className="text-xs">Pick contractor *</Label>
                <Select value={directContractorId} onValueChange={setDirectContractorId}>
                  <SelectTrigger className="mt-1" data-testid="wo-new-direct-contractor"><SelectValue placeholder="Select contractor…" /></SelectTrigger>
                  <SelectContent>
                    {contractors.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.company_name || c.name}{c.contact_name ? ` · ${c.contact_name}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-500 mt-1">Skips the RFQ stage. Use when you've already agreed on price/scope.</p>
              </div>
            )}

            {mode === "rfq" && (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <Label className="text-xs">Select contractors ({rfqContractorIds.size} picked)</Label>
                  <Input
                    placeholder="Filter contractors…"
                    value={contractorSearch}
                    onChange={(e) => setContractorSearch(e.target.value)}
                    className="h-7 w-48 text-xs"
                  />
                </div>
                <div className="max-h-52 overflow-y-auto border border-slate-200 rounded bg-white divide-y divide-slate-100">
                  {filteredContractors.length === 0 && (
                    <div className="text-center py-6 text-xs text-slate-500">No contractors match your filter.</div>
                  )}
                  {filteredContractors.map((c) => {
                    const checked = rfqContractorIds.has(c.id);
                    return (
                      <label
                        key={c.id}
                        className={`flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer hover:bg-blue-50/50 ${checked ? "bg-blue-50" : ""}`}
                        data-testid={`wo-new-rfq-contractor-${c.id}`}
                      >
                        <input type="checkbox" checked={checked} onChange={() => toggleRfq(c.id)} className="accent-blue-600" />
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-slate-900 truncate">{c.company_name || c.name}</div>
                          <div className="text-[11px] text-slate-500 truncate">{c.contact_name || ""} · {c.email || c.contact_email || ""}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </section>

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-blue-600 hover:bg-blue-700 gap-1.5"
              data-testid="wo-new-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {mode === "triage" ? "Create" : mode === "direct" ? "Create & assign" : "Create & send RFQ"}
            </Button>
          </div>
        </form>

        <CrmContactPicker
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          mode="single"
          title="Pick hospital / facility from CRM"
          onConfirm={(arr) => {
            const c = (arr && arr[0]) || null;
            if (!c) return;
            setPicked(c);
            setHospitalName(c.account_name || c.name || "");
            setContactName(c.name || "");
            setContactEmail(c.email || "");
            setContactPhone(c.phone || "");
          }}
        />

        <CrmContactPicker
          open={ccPickerOpen}
          onOpenChange={setCcPickerOpen}
          mode="multi"
          title="Add additional hospital contacts to CC"
          hospitalFilter={picked?.account_id || null}
          onConfirm={(arr) => {
            if (!arr || arr.length === 0) return;
            setCcContacts((prev) => {
              const seen = new Set(prev.map((c) => (c.email || "").toLowerCase()));
              const additions = arr
                .filter((c) => c?.email && !seen.has(c.email.toLowerCase()) && c.email.toLowerCase() !== (contactEmail || "").toLowerCase())
                .map((c) => ({ name: c.name || c.account_name || "", email: c.email }));
              return [...prev, ...additions];
            });
          }}
        />
      </div>
    </div>
  );
}
